import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import type { ClosureTrajectoryPoint } from '@/types';
import { formatConcentration } from '@/utils/format';

interface ClosureTimelineChartProps {
  trajectory: ClosureTrajectoryPoint[];
  mclThreshold?: number;
  closureP10?: number | null;
  closureMedian?: number | null;
  closureP90?: number | null;
  height?: number;
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload as ClosureTrajectoryPoint;
  return (
    <div className="bg-mono-900 border border-mono-700 rounded px-3 py-2 text-xs">
      <p className="text-mono-400 tracking-wider mb-1">QUARTER {label}</p>
      <p className="text-cyan-400">
        Max Conc: <span className="font-medium">{formatConcentration(data.max_conc_mgl)} mg/L</span>
      </p>
      <p className="text-mono-300">
        Wells Exceeding: <span className="font-medium">{data.wells_exceeding}</span>
      </p>
    </div>
  );
}

export function ClosureTimelineChart({
  trajectory,
  mclThreshold,
  closureP10,
  closureMedian,
  closureP90,
  height = 240,
}: ClosureTimelineChartProps) {
  if (!trajectory || trajectory.length === 0) {
    return (
      <div className="flex items-center justify-center text-mono-600 text-xs tracking-widest" style={{ height }}>
        NO TRAJECTORY DATA
      </div>
    );
  }

  const maxQuarter = Math.max(...trajectory.map((t) => t.quarter));

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
      <h3 className="text-xs font-semibold text-mono-300 tracking-widest mb-3">
        CLOSURE TRAJECTORY
      </h3>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={trajectory} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />

          <XAxis
            dataKey="quarter"
            tick={{ fill: '#777', fontSize: 10 }}
            tickLine={{ stroke: '#555' }}
            axisLine={{ stroke: '#555' }}
            label={{ value: 'Quarter', position: 'insideBottom', offset: -5, fill: '#777', fontSize: 10 }}
          />

          <YAxis
            scale="log"
            domain={['auto', 'auto']}
            tick={{ fill: '#777', fontSize: 10 }}
            tickLine={{ stroke: '#555' }}
            axisLine={{ stroke: '#555' }}
            tickFormatter={(v: number) => formatConcentration(v)}
            label={{ value: 'mg/L', angle: -90, position: 'insideLeft', fill: '#777', fontSize: 10 }}
          />

          <Tooltip content={<CustomTooltip />} />

          {/* Closure p10-p90 band */}
          {closureP10 != null && closureP90 != null && closureP90 <= maxQuarter && (
            <ReferenceArea
              x1={closureP10}
              x2={closureP90}
              fill="#22c55e"
              fillOpacity={0.08}
              stroke="#22c55e"
              strokeOpacity={0.2}
              strokeDasharray="3 3"
            />
          )}

          {/* MCL threshold line */}
          {mclThreshold != null && (
            <ReferenceLine
              y={mclThreshold}
              stroke="#ef4444"
              strokeDasharray="6 3"
              label={{
                value: 'MCL',
                position: 'right',
                fill: '#ef4444',
                fontSize: 10,
              }}
            />
          )}

          {/* Median closure quarter */}
          {closureMedian != null && closureMedian <= maxQuarter && (
            <ReferenceLine
              x={closureMedian}
              stroke="#22c55e"
              strokeDasharray="6 3"
              label={{
                value: `Median Closure (Q${closureMedian})`,
                position: 'top',
                fill: '#22c55e',
                fontSize: 9,
              }}
            />
          )}

          {/* Main concentration line */}
          <Line
            type="monotone"
            dataKey="max_conc_mgl"
            stroke="#06b6d4"
            strokeWidth={2}
            dot={{ fill: '#06b6d4', r: 2 }}
            activeDot={{ fill: '#06b6d4', r: 4, stroke: '#fff', strokeWidth: 1 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
